/**
 * API Guide Modal Component
 * 
 * API 키 사용 가이드 모달
 */

import { useState } from 'react';
import { useLanguage } from '../i18n';
import { useBodyScrollLock } from '../hooks';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function ApiGuideModal({ isOpen, onClose }: Props) {
  const { lang } = useLanguage();
  const content = lang === 'ko' ? koContent : enContent;
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const handleCopy = async (code: string, id: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // 모달 열릴 때 배경 스크롤 방지
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 백드롭 */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* 모달 */}
      <div className="relative bg-terminal-bg border border-terminal-border rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* 헤더 */}
        <div className="sticky top-0 z-10 px-6 py-4 border-b border-terminal-border bg-terminal-bg/95 backdrop-blur">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-terminal-text flex items-center gap-2">
                <svg className="w-5 h-5 text-status-go" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                API Reference
              </h2>
              <p className="text-sm text-terminal-muted mt-0.5">{content.subtitle}</p>
            </div>
            <button
              onClick={onClose}
              className="text-terminal-muted hover:text-terminal-text transition-colors p-1"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* 컨텐츠 */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(90vh-80px)] space-y-8">
          {/* Base URL */}
          <section>
            <div className="p-4 bg-terminal-dim/30 border border-terminal-border rounded-lg">
              <code className="text-status-go text-sm">Base URL: https://api.releasegate.app</code>
            </div>
          </section>

          {/* 인증 */}
          <section>
            <h3 className="text-lg font-semibold text-terminal-text mb-4">{content.auth.title}</h3>
            
            {/* 경고 */}
            <div className="p-3 bg-status-warning/10 border border-status-warning/30 rounded-lg mb-4">
              <p className="text-status-warning text-sm font-medium">⚠️ {content.auth.ownerOnly}</p>
            </div>

            {/* 발급 절차 */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-terminal-text mb-2">{content.auth.steps.title}</h4>
              <ol className="list-decimal list-inside space-y-1 text-terminal-muted text-sm">
                {content.auth.steps.items.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>

            {/* 권한 표 */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-terminal-border rounded-lg overflow-hidden">
                <thead className="bg-terminal-dim/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-terminal-text border-b border-terminal-border">{content.auth.table.role}</th>
                    <th className="px-3 py-2 text-center text-terminal-text border-b border-terminal-border">{content.auth.table.create}</th>
                    <th className="px-3 py-2 text-center text-terminal-text border-b border-terminal-border">{content.auth.table.viewList}</th>
                    <th className="px-3 py-2 text-center text-terminal-text border-b border-terminal-border">{content.auth.table.use}</th>
                    <th className="px-3 py-2 text-center text-terminal-text border-b border-terminal-border">{content.auth.table.revoke}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-3 py-2 text-terminal-muted border-b border-terminal-border">{content.auth.table.owner}</td>
                    <td className="px-3 py-2 text-center text-status-go border-b border-terminal-border">✓</td>
                    <td className="px-3 py-2 text-center text-status-go border-b border-terminal-border">✓</td>
                    <td className="px-3 py-2 text-center text-status-go border-b border-terminal-border">✓</td>
                    <td className="px-3 py-2 text-center text-status-go border-b border-terminal-border">✓</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-terminal-muted">{content.auth.table.member}</td>
                    <td className="px-3 py-2 text-center text-status-danger">✗</td>
                    <td className="px-3 py-2 text-center text-status-go">✓</td>
                    <td className="px-3 py-2 text-center text-status-go">✓</td>
                    <td className="px-3 py-2 text-center text-status-danger">✗</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-terminal-muted text-xs mt-2">{content.auth.table.note}</p>
          </section>

          {/* 제한 사항 */}
          <section>
            <h3 className="text-lg font-semibold text-terminal-text mb-4">{content.limits.title}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-terminal-border rounded-lg overflow-hidden">
                <thead className="bg-terminal-dim/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-terminal-text border-b border-terminal-border">{content.limits.item}</th>
                    <th className="px-3 py-2 text-left text-terminal-text border-b border-terminal-border">{content.limits.limit}</th>
                  </tr>
                </thead>
                <tbody>
                  {content.limits.items.map((item, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 text-terminal-muted border-b border-terminal-border">{item.name}</td>
                      <td className="px-3 py-2 text-terminal-text border-b border-terminal-border">{item.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-terminal-muted text-xs mt-2">{content.limits.note}</p>
          </section>

          {/* 엔드포인트 */}
          <section>
            <h3 className="text-lg font-semibold text-terminal-text mb-4">{content.endpoint.title}</h3>
            
            {/* POST /api/evaluate */}
            <div className="p-3 bg-terminal-dim/20 border border-terminal-border rounded-lg mb-4">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-status-go text-white text-xs font-bold rounded">POST</span>
                <code className="text-terminal-text font-mono text-sm">/api/evaluate</code>
              </div>
              <p className="text-terminal-muted text-sm mt-1">{content.endpoint.desc}</p>
            </div>

            {/* 요청 헤더 */}
            <h4 className="text-sm font-medium text-terminal-text mb-2">{content.endpoint.headers}</h4>
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm border border-terminal-border rounded-lg overflow-hidden">
                <thead className="bg-terminal-dim/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-terminal-text border-b border-terminal-border">{content.endpoint.header}</th>
                    <th className="px-3 py-2 text-center text-terminal-text border-b border-terminal-border">{content.endpoint.required}</th>
                    <th className="px-3 py-2 text-left text-terminal-text border-b border-terminal-border">{content.endpoint.description}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-3 py-2 font-mono text-terminal-muted border-b border-terminal-border">X-API-Key</td>
                    <td className="px-3 py-2 text-center text-status-go border-b border-terminal-border">Yes</td>
                    <td className="px-3 py-2 text-terminal-muted border-b border-terminal-border">API Key</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-terminal-muted">Content-Type</td>
                    <td className="px-3 py-2 text-center text-status-go">Yes</td>
                    <td className="px-3 py-2 text-terminal-muted">application/json</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 요청 필드 */}
            <h4 className="text-sm font-medium text-terminal-text mb-2">{content.endpoint.fields.title}</h4>
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm border border-terminal-border rounded-lg overflow-hidden">
                <thead className="bg-terminal-dim/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-terminal-text border-b border-terminal-border">{content.endpoint.fields.field}</th>
                    <th className="px-3 py-2 text-left text-terminal-text border-b border-terminal-border">{content.endpoint.fields.type}</th>
                    <th className="px-3 py-2 text-center text-terminal-text border-b border-terminal-border">{content.endpoint.required}</th>
                    <th className="px-3 py-2 text-left text-terminal-text border-b border-terminal-border">{content.endpoint.description}</th>
                  </tr>
                </thead>
                <tbody>
                  {content.endpoint.fields.items.map((field, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-mono text-terminal-muted border-b border-terminal-border">{field.name}</td>
                      <td className="px-3 py-2 text-terminal-muted border-b border-terminal-border">{field.type}</td>
                      <td className="px-3 py-2 text-center border-b border-terminal-border">
                        {field.required ? <span className="text-status-go">Yes</span> : <span className="text-terminal-muted">No</span>}
                      </td>
                      <td className="px-3 py-2 text-terminal-muted border-b border-terminal-border">{field.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* QA 상태 값 */}
            <h4 className="text-sm font-medium text-terminal-text mb-2">{content.endpoint.qaStatus.title}</h4>
            <ul className="text-sm text-terminal-muted space-y-1 mb-4">
              <li><code className="text-status-go">PASS</code> - {content.endpoint.qaStatus.pass}</li>
              <li><code className="text-status-warning">PARTIAL</code> - {content.endpoint.qaStatus.partial}</li>
              <li><code className="text-status-danger">FAIL</code> - {content.endpoint.qaStatus.fail}</li>
            </ul>
          </section>

          {/* 추가 엔드포인트 */}
          <section>
            <h3 className="text-lg font-semibold text-terminal-text mb-4">{content.moreEndpoints.title}</h3>
            <div className="space-y-4">
              {content.moreEndpoints.items.map((ep, i) => (
                <div key={i} className="p-4 bg-terminal-dim/20 border border-terminal-border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 text-white text-xs font-bold rounded ${ep.method === 'GET' ? 'bg-blue-500' : 'bg-status-go'}`}>
                      {ep.method}
                    </span>
                    <code className="text-terminal-text font-mono text-sm">{ep.path}</code>
                  </div>
                  <p className="text-terminal-muted text-sm mb-3">{ep.desc}</p>
                  
                  {/* 파라미터 */}
                  {ep.params && ep.params.length > 0 && (
                    <div className="mb-3">
                      <h5 className="text-xs font-medium text-terminal-text mb-1">{content.moreEndpoints.params}</h5>
                      <div className="text-xs text-terminal-muted space-y-0.5">
                        {ep.params.map((p, j) => (
                          <div key={j}><code className="text-status-go">{p.name}</code>: {p.desc}</div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* 요청 본문 */}
                  {ep.body && (
                    <div className="mb-3">
                      <h5 className="text-xs font-medium text-terminal-text mb-1">{content.moreEndpoints.requestBody}</h5>
                      <pre className="p-2 bg-[#1a1a2e] border border-terminal-border rounded text-xs overflow-x-auto">
                        <code className="text-terminal-text">{ep.body}</code>
                      </pre>
                    </div>
                  )}
                  
                  {/* 응답 */}
                  {ep.response && (
                    <div>
                      <h5 className="text-xs font-medium text-terminal-text mb-1">{content.moreEndpoints.response}</h5>
                      <pre className="p-2 bg-[#1a1a2e] border border-terminal-border rounded text-xs overflow-x-auto">
                        <code className="text-terminal-text">{ep.response}</code>
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* 응답 상태 */}
          <section>
            <h3 className="text-lg font-semibold text-terminal-text mb-4">{content.response.title}</h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 bg-status-go/10 border border-status-go/30 rounded text-sm">
                <span className="text-status-go font-mono">go</span>
                <span className="text-terminal-muted ml-2">🟢 {content.response.go}</span>
              </div>
              <div className="p-2 bg-status-warning/10 border border-status-warning/30 rounded text-sm">
                <span className="text-status-warning font-mono">conditional</span>
                <span className="text-terminal-muted ml-2">🟡 {content.response.conditional}</span>
              </div>
              <div className="p-2 bg-status-holding/10 border border-status-holding/30 rounded text-sm">
                <span className="text-status-holding font-mono">holding</span>
                <span className="text-terminal-muted ml-2">🟠 {content.response.holding}</span>
              </div>
              <div className="p-2 bg-status-danger/10 border border-status-danger/30 rounded text-sm">
                <span className="text-status-danger font-mono">no_go</span>
                <span className="text-terminal-muted ml-2">🔴 {content.response.noGo}</span>
              </div>
            </div>
          </section>

          {/* 코드 예시 */}
          <section>
            <h3 className="text-lg font-semibold text-terminal-text mb-4">{content.examples.title}</h3>
            
            {/* cURL */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-terminal-text">cURL</h4>
                <button
                  onClick={() => handleCopy(content.examples.curl, 'curl')}
                  className="px-2 py-1 text-xs text-terminal-muted hover:text-terminal-text bg-terminal-dim hover:bg-terminal-border rounded transition-colors"
                >
                  {copiedCode === 'curl' ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="p-3 bg-[#1a1a2e] border border-terminal-border rounded-lg overflow-x-auto text-sm">
                <code className="text-terminal-text whitespace-pre">{content.examples.curl}</code>
              </pre>
            </div>

            {/* Python */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-terminal-text">Python</h4>
                <button
                  onClick={() => handleCopy(content.examples.python, 'python')}
                  className="px-2 py-1 text-xs text-terminal-muted hover:text-terminal-text bg-terminal-dim hover:bg-terminal-border rounded transition-colors"
                >
                  {copiedCode === 'python' ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="p-3 bg-[#1a1a2e] border border-terminal-border rounded-lg overflow-x-auto text-sm">
                <code className="text-terminal-text whitespace-pre">{content.examples.python}</code>
              </pre>
            </div>

            {/* JavaScript */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-terminal-text">JavaScript</h4>
                <button
                  onClick={() => handleCopy(content.examples.javascript, 'js')}
                  className="px-2 py-1 text-xs text-terminal-muted hover:text-terminal-text bg-terminal-dim hover:bg-terminal-border rounded transition-colors"
                >
                  {copiedCode === 'js' ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="p-3 bg-[#1a1a2e] border border-terminal-border rounded-lg overflow-x-auto text-sm">
                <code className="text-terminal-text whitespace-pre">{content.examples.javascript}</code>
              </pre>
            </div>
          </section>

          {/* 에러 코드 */}
          <section>
            <h3 className="text-lg font-semibold text-terminal-text mb-4">{content.errors.title}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-terminal-border rounded-lg overflow-hidden">
                <thead className="bg-terminal-dim/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-terminal-text border-b border-terminal-border">HTTP</th>
                    <th className="px-3 py-2 text-left text-terminal-text border-b border-terminal-border">{content.errors.meaning}</th>
                    <th className="px-3 py-2 text-left text-terminal-text border-b border-terminal-border">{content.errors.solution}</th>
                  </tr>
                </thead>
                <tbody>
                  {content.errors.items.map((item, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-mono text-terminal-text border-b border-terminal-border">{item.code}</td>
                      <td className="px-3 py-2 text-terminal-muted border-b border-terminal-border">{item.meaning}</td>
                      <td className="px-3 py-2 text-terminal-muted border-b border-terminal-border">{item.solution}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

// 한국어 콘텐츠
const koContent = {
  subtitle: 'API를 통해 릴리즈 평가를 자동화하세요',
  auth: {
    title: '인증',
    ownerOnly: '팀 오너만 API 키를 생성할 수 있습니다.',
    steps: {
      title: 'API 키 발급 절차',
      items: [
        'Team 플랜 구독 (팀 오너)',
        '사용자 메뉴 → "API 키" 클릭',
        '"새 API 키 생성" 버튼 클릭',
        '키 이름 입력 후 생성',
        '생성된 키를 즉시 복사 (다시 표시되지 않음!)',
      ],
    },
    table: {
      role: '역할',
      create: '생성',
      viewList: '목록 조회',
      use: '사용',
      revoke: '폐기',
      owner: '팀 오너',
      member: '팀 멤버',
      note: '* 목록 조회: 키 이름, prefix, 사용량만 확인 가능. 전체 키는 생성 시 1회만 표시됩니다.',
    },
  },
  limits: {
    title: '제한 사항',
    item: '항목',
    limit: '제한',
    items: [
      { name: '일일 요청 수', value: '200 req/day' },
      { name: '최대 API 키 개수', value: '5개' },
      { name: '테스트 케이스 수', value: '최대 200개' },
      { name: 'Planning 텍스트', value: '최대 50,000자' },
    ],
    note: '일일 사용량은 매일 00:00 KST에 리셋됩니다.',
  },
  endpoint: {
    title: '엔드포인트',
    desc: '릴리즈 정보를 평가하고 Release Confidence Score를 반환합니다.',
    headers: '요청 헤더',
    header: '헤더',
    required: '필수',
    description: '설명',
    fields: {
      title: '요청 필드',
      field: '필드',
      type: '타입',
      items: [
        { name: 'feature_name', type: 'string', required: true, desc: '기능명 (1-200자)' },
        { name: 'release_date', type: 'string', required: true, desc: '릴리즈 예정일' },
        { name: 'planning_intent', type: 'string[]', required: false, desc: '기획 의도 목록' },
        { name: 'risks', type: 'string[]', required: false, desc: '릴리즈 리스크 목록' },
        { name: 'qa_status', type: 'object', required: false, desc: 'QA 상태 (테스트케이스: 상태)' },
        { name: 'lang', type: 'string', required: false, desc: '응답 언어 (ko/en, 기본: ko)' },
      ],
    },
    qaStatus: {
      title: 'qa_status 상태 값',
      pass: '검증 완료',
      partial: '일부 검증/미검증',
      fail: '미검증/실패',
    },
  },
  moreEndpoints: {
    title: '추가 엔드포인트',
    params: '파라미터',
    requestBody: '요청 본문',
    response: '응답 예시',
    items: [
      { 
        method: 'GET', 
        path: '/api/history', 
        desc: '평가 히스토리 목록 조회',
        params: [
          { name: 'page', desc: '페이지 번호 (기본값: 1)' },
          { name: 'per_page', desc: '페이지당 항목 수 (기본값: 5, 최대: 20)' },
          { name: 'team_id', desc: '팀 ID (미지정 시 개인 히스토리)' },
        ],
        response: `{
  "items": [
    {
      "id": 123,
      "feature_name": "신규 결제 기능",
      "confidence_score": 72,
      "status": "conditional",
      "created_at": "2025-01-10T09:30:00Z"
    }
  ],
  "total": 50,
  "page": 1,
  "total_pages": 10
}`,
      },
      { 
        method: 'GET', 
        path: '/api/history/{id}', 
        desc: '특정 평가 기록의 상세 정보 조회',
        response: `{
  "id": 123,
  "feature_name": "신규 결제 기능",
  "release_date": "2025-01-15",
  "result": { /* ReleaseDecision 객체 */ },
  "input_data": { /* ReleaseInput 객체 */ },
  "created_at": "2025-01-10T09:30:00Z"
}`,
      },
      { 
        method: 'GET', 
        path: '/api/statistics', 
        desc: '평가 통계 조회 (Pro 이상 플랜)',
        params: [
          { name: 'days', desc: '조회 기간 (기본값: 30, 최대: 90)' },
        ],
        response: `{
  "total_evaluations": 50,
  "avg_score": 72.5,
  "daily_evaluations": [
    {"date": "2025-01-10", "count": 5}
  ],
  "status_distribution": {
    "go": 20, "conditional": 15,
    "holding": 10, "no_go": 5
  },
  "period_days": 30
}`,
      },
      { 
        method: 'POST', 
        path: '/api/report/pdf', 
        desc: '평가 결과를 PDF 리포트로 생성 (Pro 이상 플랜)',
        body: `{
  "result": { /* ReleaseDecision 객체 */ },
  "input_data": { /* ReleaseInput 객체 */ },
  "lang": "ko"
}`,
        response: 'application/pdf (바이너리)',
      },
      { 
        method: 'GET', 
        path: '/api/report/pdf/{id}', 
        desc: '저장된 평가 기록을 PDF로 다운로드 (Pro 이상 플랜)',
        params: [
          { name: 'lang', desc: '언어 (ko 또는 en, 기본값: ko)' },
        ],
        response: 'application/pdf (바이너리)',
      },
    ],
  },
  response: {
    title: '응답 상태',
    go: '출시 가능',
    conditional: '조건부 출시',
    holding: '출시 보류',
    noGo: '출시 불가',
  },
  examples: {
    title: '코드 예시',
    curl: `curl -X POST https://api.releasegate.app/api/evaluate \\
  -H "X-API-Key: sk-your-api-key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "feature_name": "신규 결제 기능",
    "release_date": "2025-01-15",
    "planning_intent": ["결제 전환율 향상"],
    "qa_status": {
      "핵심 결제 플로우": "PASS",
      "Edge Case": "PARTIAL"
    },
    "lang": "ko"
  }'`,
    python: `import requests

response = requests.post(
    "https://api.releasegate.app/api/evaluate",
    headers={
        "X-API-Key": "sk-your-api-key",
        "Content-Type": "application/json"
    },
    json={
        "feature_name": "신규 결제 기능",
        "release_date": "2025-01-15",
        "planning_intent": ["결제 전환율 향상"],
        "qa_status": {"핵심 결제 플로우": "PASS"},
        "lang": "ko"
    }
)
print(response.json())`,
    javascript: `const response = await fetch(
  "https://api.releasegate.app/api/evaluate",
  {
    method: "POST",
    headers: {
      "X-API-Key": "sk-your-api-key",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      feature_name: "신규 결제 기능",
      release_date: "2025-01-15",
      planning_intent: ["결제 전환율 향상"],
      qa_status: { "핵심 결제 플로우": "PASS" },
      lang: "ko",
    }),
  }
);
const result = await response.json();
console.log(result);`,
  },
  errors: {
    title: '에러 코드',
    meaning: '의미',
    solution: '해결 방법',
    items: [
      { code: '400', meaning: '잘못된 요청', solution: '요청 본문 형식 확인' },
      { code: '401', meaning: '인증 실패', solution: 'API 키 확인' },
      { code: '403', meaning: '권한 없음', solution: 'Team 플랜 구독 필요' },
      { code: '429', meaning: '요청 제한 초과', solution: '00:00 KST 이후 재시도' },
      { code: '500', meaning: '서버 오류', solution: '잠시 후 재시도' },
    ],
  },
};

// 영어 콘텐츠
const enContent = {
  subtitle: 'Automate release evaluation via API',
  auth: {
    title: 'Authentication',
    ownerOnly: 'Only team owners can create API keys.',
    steps: {
      title: 'API Key Issuance',
      items: [
        'Subscribe to Team plan (as team owner)',
        'User menu → "API Keys"',
        'Click "Create New API Key"',
        'Enter key name and create',
        'Copy the key immediately (not shown again!)',
      ],
    },
    table: {
      role: 'Role',
      create: 'Create',
      viewList: 'View List',
      use: 'Use',
      revoke: 'Revoke',
      owner: 'Team Owner',
      member: 'Team Member',
      note: '* View List: Only key name, prefix, and usage visible. Full key shown only once at creation.',
    },
  },
  limits: {
    title: 'Limits',
    item: 'Item',
    limit: 'Limit',
    items: [
      { name: 'Daily requests', value: '200 req/day' },
      { name: 'Max API keys', value: '5' },
      { name: 'Test cases', value: 'Max 200' },
      { name: 'Planning text', value: 'Max 50,000 chars' },
    ],
    note: 'Daily usage resets at 00:00 KST.',
  },
  endpoint: {
    title: 'Endpoint',
    desc: 'Evaluate release info and return Release Confidence Score.',
    headers: 'Request Headers',
    header: 'Header',
    required: 'Required',
    description: 'Description',
    fields: {
      title: 'Request Fields',
      field: 'Field',
      type: 'Type',
      items: [
        { name: 'feature_name', type: 'string', required: true, desc: 'Feature name (1-200 chars)' },
        { name: 'release_date', type: 'string', required: true, desc: 'Release date' },
        { name: 'planning_intent', type: 'string[]', required: false, desc: 'Planning intent list' },
        { name: 'risks', type: 'string[]', required: false, desc: 'Release risks list' },
        { name: 'qa_status', type: 'object', required: false, desc: 'QA status (testcase: status)' },
        { name: 'lang', type: 'string', required: false, desc: 'Response lang (ko/en, default: ko)' },
      ],
    },
    qaStatus: {
      title: 'qa_status Values',
      pass: 'Verified',
      partial: 'Partially verified',
      fail: 'Not verified/Failed',
    },
  },
  moreEndpoints: {
    title: 'More Endpoints',
    params: 'Parameters',
    requestBody: 'Request Body',
    response: 'Response Example',
    items: [
      { 
        method: 'GET', 
        path: '/api/history', 
        desc: 'List evaluation history',
        params: [
          { name: 'page', desc: 'Page number (default: 1)' },
          { name: 'per_page', desc: 'Items per page (default: 5, max: 20)' },
          { name: 'team_id', desc: 'Team ID (omit for personal history)' },
        ],
        response: `{
  "items": [
    {
      "id": 123,
      "feature_name": "New Payment Feature",
      "confidence_score": 72,
      "status": "conditional",
      "created_at": "2025-01-10T09:30:00Z"
    }
  ],
  "total": 50,
  "page": 1,
  "total_pages": 10
}`,
      },
      { 
        method: 'GET', 
        path: '/api/history/{id}', 
        desc: 'Get detailed evaluation record',
        response: `{
  "id": 123,
  "feature_name": "New Payment Feature",
  "release_date": "2025-01-15",
  "result": { /* ReleaseDecision */ },
  "input_data": { /* ReleaseInput */ },
  "created_at": "2025-01-10T09:30:00Z"
}`,
      },
      { 
        method: 'GET', 
        path: '/api/statistics', 
        desc: 'Get evaluation statistics (Pro+ plan)',
        params: [
          { name: 'days', desc: 'Period in days (default: 30, max: 90)' },
        ],
        response: `{
  "total_evaluations": 50,
  "avg_score": 72.5,
  "daily_evaluations": [
    {"date": "2025-01-10", "count": 5}
  ],
  "status_distribution": {
    "go": 20, "conditional": 15,
    "holding": 10, "no_go": 5
  },
  "period_days": 30
}`,
      },
      { 
        method: 'POST', 
        path: '/api/report/pdf', 
        desc: 'Generate PDF report from evaluation (Pro+ plan)',
        body: `{
  "result": { /* ReleaseDecision */ },
  "input_data": { /* ReleaseInput */ },
  "lang": "en"
}`,
        response: 'application/pdf (binary)',
      },
      { 
        method: 'GET', 
        path: '/api/report/pdf/{id}', 
        desc: 'Download saved evaluation as PDF (Pro+ plan)',
        params: [
          { name: 'lang', desc: 'Language (ko or en, default: ko)' },
        ],
        response: 'application/pdf (binary)',
      },
    ],
  },
  response: {
    title: 'Response Status',
    go: 'Ready to release',
    conditional: 'Conditional',
    holding: 'On hold',
    noGo: 'Not ready',
  },
  examples: {
    title: 'Code Examples',
    curl: `curl -X POST https://api.releasegate.app/api/evaluate \\
  -H "X-API-Key: sk-your-api-key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "feature_name": "New Payment Feature",
    "release_date": "2025-01-15",
    "planning_intent": ["Improve conversion"],
    "qa_status": {
      "Core Payment Flow": "PASS",
      "Edge Case": "PARTIAL"
    },
    "lang": "en"
  }'`,
    python: `import requests

response = requests.post(
    "https://api.releasegate.app/api/evaluate",
    headers={
        "X-API-Key": "sk-your-api-key",
        "Content-Type": "application/json"
    },
    json={
        "feature_name": "New Payment Feature",
        "release_date": "2025-01-15",
        "planning_intent": ["Improve conversion"],
        "qa_status": {"Core Payment Flow": "PASS"},
        "lang": "en"
    }
)
print(response.json())`,
    javascript: `const response = await fetch(
  "https://api.releasegate.app/api/evaluate",
  {
    method: "POST",
    headers: {
      "X-API-Key": "sk-your-api-key",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      feature_name: "New Payment Feature",
      release_date: "2025-01-15",
      planning_intent: ["Improve conversion"],
      qa_status: { "Core Payment Flow": "PASS" },
      lang: "en",
    }),
  }
);
const result = await response.json();
console.log(result);`,
  },
  errors: {
    title: 'Error Codes',
    meaning: 'Meaning',
    solution: 'Solution',
    items: [
      { code: '400', meaning: 'Bad Request', solution: 'Check request body' },
      { code: '401', meaning: 'Unauthorized', solution: 'Check API key' },
      { code: '403', meaning: 'Forbidden', solution: 'Team plan required' },
      { code: '429', meaning: 'Rate limited', solution: 'Retry after 00:00 KST' },
      { code: '500', meaning: 'Server error', solution: 'Retry later' },
    ],
  },
};

